import React, { useEffect, useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StepLabel from '@mui/material/StepLabel';
import ActionTypes from '../../../journeyState/InsuranceJourney/ActionTypes';
import { InsuranceStepperStatus } from '../../common/CommonTypes';
import { PageAbsolutePath } from '../../../consts/Common';

type InsuranceNavStepperProps = {
  insuranceNavigationStatus: InsuranceStepperStatus;
  currentPath: string;
};

export const GetNavDispatch = (
  page: string,
  status: boolean
): ActionTypes | undefined => {
  switch (page) {
    case PageAbsolutePath.Insurance.AboutYou:
    case PageAbsolutePath.Insurance.DrivingHistory: {
      return {
        type: 'set-nav-status',
        key: 'aboutyou',
        value: status
      };
    }
    case PageAbsolutePath.Insurance.VehicleDetails: {
      return {
        type: 'set-nav-status',
        key: 'vehicleDetails',
        value: status
      };
    }
    case PageAbsolutePath.Insurance.InsuranceCoverDetailsPage:
    case PageAbsolutePath.Insurance.AddressDetailsPage: {
      return {
        type: 'set-nav-status',
        key: 'coverDetails',
        value: status
      };
    }
    case PageAbsolutePath.Insurance.AdditionalDriverDetailsPage:
    case PageAbsolutePath.Insurance.ConfirmationPage: {
      return {
        type: 'set-nav-status',
        key: 'additonalDriverDetails',
        value: status
      };
    }
    case PageAbsolutePath.Insurance.YourQuote:
    case PageAbsolutePath.Insurance.OptionalCover: {
      return {
        type: 'set-nav-status',
        key: 'yourQuote',
        value: status
      };
    }
    case PageAbsolutePath.Insurance.CheckYourDetails:
    case PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice:
    case PageAbsolutePath.Insurance.PaymentPage:
    case PageAbsolutePath.Insurance.PolicyConfirmation: {
      return {
        type: 'set-nav-status',
        key: 'confirmation',
        value: status
      };
    }
    default: {
      return undefined;
    }
  }
};
const InsuranceNavStepper = React.memo(
  function NavStepper({
    insuranceNavigationStatus,
    currentPath
  }: InsuranceNavStepperProps): JSX.Element {
    const stepsArray = [
      { stepName: 'About you', completed: false },
      { stepName: 'Your vehicle', completed: false },
      { stepName: 'Cover details', completed: false },
      { stepName: 'Additional drivers', completed: false },
      { stepName: 'Your quote', completed: false },
      { stepName: 'Buy', completed: false }
    ];

    const [steps, setSteps] = useState(stepsArray);
    const numberOfSteps = steps.length;

    const isStepCompleted = (stepId: number): boolean => {
      switch (stepId) {
        case 0: {
          return insuranceNavigationStatus.aboutyou;
        }
        case 1: {
          return insuranceNavigationStatus.vehicleDetails;
        }
        case 2: {
          return insuranceNavigationStatus.coverDetails;
        }
        case 3: {
          return insuranceNavigationStatus.additonalDriverDetails;
        }
        case 4: {
          return insuranceNavigationStatus.yourQuote;
        }
        case 5: {
          return insuranceNavigationStatus.confirmation;
        }

        default: {
          return false;
        }
      }
    };

    const getActiveStepIndex = (): number => {
      switch (currentPath) {
        case PageAbsolutePath.Insurance.AboutYou:
        case PageAbsolutePath.Insurance.DrivingHistory: {
          return 0;
        }
        case PageAbsolutePath.Insurance.VehicleDetails: {
          return 1;
        }
        case PageAbsolutePath.Insurance.InsuranceCoverDetailsPage:
        case PageAbsolutePath.Insurance.AddressDetailsPage: {
          return 2;
        }
        case PageAbsolutePath.Insurance.AdditionalDriverDetailsPage:
        case PageAbsolutePath.Insurance.ConfirmationPage: {
          return 3;
        }
        case PageAbsolutePath.Insurance.YourQuote:
        case PageAbsolutePath.Insurance.OptionalCover: {
          return 4;
        }
        case PageAbsolutePath.Insurance.CheckYourDetails:
        case PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice:
        case PageAbsolutePath.Insurance.PaymentPage:
        case PageAbsolutePath.Insurance.PolicyConfirmation: {
          return 5;
        }
        default: {
          return 0;
        }
      }
    };

    const getLabelContent = (index: number, activeStep: boolean) => {
      const step = steps[index];
      const completedStep = step.completed;
      const label = step.stepName;

      return (
        <StepLabel
          icon={
            activeStep && completedStep ? (
              <CheckCircleIcon className='breadcrumb-active-label' />
            ) : completedStep ? (
              <CheckCircleOutlineIcon style={{ color: 'green' }} />
            ) : (
              index + 1
            )
          }>
          <span
            className={
              activeStep && !completedStep
                ? 'breadcrumb-active-label'
                : 'breadcrumb-root-label'
            }>
            {label}
          </span>
        </StepLabel>
      );
    };

    const getMobileContent = () => {
      const isErrorPage = getActiveStepIndex() > numberOfSteps - 1;

      const indexText = isErrorPage
        ? ''
        : `Step ${getActiveStepIndex() + 1} of 6`;

      const nameText = isErrorPage
        ? 'Error'
        : steps[getActiveStepIndex()].stepName;

      return (
        <div style={{ paddingTop: '10px' }}>
          <span style={{ color: 'rgba(31,31,31,1)', fontSize: '14px' }}>
            {indexText}
          </span>
          <br />
          <span className='breadcrumb-mobile-active-label'>{nameText}</span>
        </div>
      );
    };

    const updateBreadcrumb = () => {
      setSteps(
        steps.map((s, index) => ({ ...s, completed: isStepCompleted(index) }))
      );
    };

    useEffect(() => {
      updateBreadcrumb();
    }, [currentPath, insuranceNavigationStatus]);

    const activeStepIndex = getActiveStepIndex();

    return (
      <div>
        <div className='mobile-hide navbar-desktop'>
          <Stepper activeStep={activeStepIndex} nonLinear={true}>
            {steps.map((step, index) => {
              return (
                <Step key={step.stepName}>
                  {getLabelContent(index, index === activeStepIndex)}
                </Step>
              );
            })}
          </Stepper>
          <br />
        </div>
        <div className='desktop-hide'>
          <div className='mobile-navbar'>{getMobileContent()}</div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.insuranceNavigationStatus ===
        nextProps.insuranceNavigationStatus &&
      prevProps.currentPath === nextProps.currentPath
    ) {
      return true;
    }
    return false;
  }
);

export default InsuranceNavStepper;
